// Pure helpers behind the bulk "Remove Access" action on the Members tab.
//
// Two things here are easy to get wrong, so they live in one tested place
// rather than inline in the component:
//
//  1. A selection is a list of row ids, and rows go stale — they disappear on a
//     refresh or a workspace switch, and owners/admins were never removable to
//     begin with. Resolving ids to members has to happen before the request so
//     the request and the message about it can't disagree.
//
//  2. POST /api/v1/org/members/remove/ answers **200 even when it refuses
//     individual members**. What actually happened is in `removed_user_ids`,
//     `errors` and `reassignment_errors`. Counting from the request size instead
//     reports removals that never happened.

export const errorMessage = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || "";

/**
 * Resolve selected row ids to the members that can actually be removed.
 * Drops rows that vanished, owners, admins, the caller themselves (the backend
 * refuses that anyway) and duplicate user ids.
 */
export const selectRemovableMembers = (
  selectedIds = [],
  teamMembers = [],
  currentUserId = null
) => {
  const seenUserIds = new Set();
  return selectedIds
    .map((id) => teamMembers.find((m) => m.id === id))
    .filter((member) => {
      if (!member || !member.user_id) return false;
      if (member.isOwner || member.role === "admin") return false;
      if (currentUserId != null && member.user_id === currentUserId) return false;
      if (seenUserIds.has(member.user_id)) return false;
      seenUserIds.add(member.user_id);
      return true;
    });
};

/**
 * One request per workspace: `[[orgId, [userId, ...]], ...]`, in selection order.
 */
export const groupByOrg = (members = []) => {
  const byOrg = new Map();
  members.forEach((member) => {
    const key = member.orgId;
    if (!byOrg.has(key)) byOrg.set(key, []);
    byOrg.get(key).push(member.user_id);
  });
  return Array.from(byOrg.entries());
};

/**
 * Tally what a set of removal requests actually achieved.
 *
 * @param groups  the `groupByOrg` output the requests were built from
 * @param settled the matching `Promise.allSettled` results
 */
export const summarizeRemoval = (groups = [], settled = []) => {
  const outcome = {
    removed: 0,
    failed: 0,
    reassigned: 0,
    reassignFailed: 0,
    strandedNames: [],
    error: "",
  };

  settled.forEach((result, index) => {
    const attempted = (groups[index]?.[1] || []).length;

    if (result.status !== "fulfilled") {
      outcome.failed += attempted;
      if (!outcome.error) outcome.error = errorMessage(result.reason);
      return;
    }

    const data = result.value?.data || {};
    const removed = (data.removed_user_ids || []).length;
    outcome.removed += removed;
    // Never let a miscounted response report more removals than were asked for.
    outcome.failed += Math.max(0, attempted - removed);
    outcome.reassigned += (data.reassigned_projects || []).length;

    const stranded = data.reassignment_errors || [];
    outcome.reassignFailed += stranded.length;
    // A count alone means hunting the Projects tab for the one that's stuck, so
    // carry whatever the server named through to the message.
    stranded.forEach((entry) => {
      const name = entry?.name || (entry?.folder_id ? `#${entry.folder_id}` : "");
      if (name) outcome.strandedNames.push(name);
    });

    if (!outcome.error && removed < attempted) {
      outcome.error = data.errors?.[0]?.error || stranded[0]?.error || "";
    }
  });

  return outcome;
};

/**
 * The message for projects a removal left behind, or "" when none were.
 * A project nobody owns is stuck until someone transfers it, so this never
 * collapses into the success toast.
 */
export const strandedMessage = (outcome) => {
  if (!outcome?.reassignFailed) return "";
  const named = describeList(outcome.strandedNames);
  if (!named) {
    return `${outcome.reassignFailed} project(s) couldn't be reassigned. Transfer them from the project menu.`;
  }
  return `Couldn't reassign ${named}. Transfer ${
    outcome.reassignFailed === 1 ? "it" : "them"
  } from the project menu.`;
};

/**
 * "Alpha, Beta and 3 more" — a readable list that can't run away with the toast.
 */
export const describeList = (names = [], limit = 2) => {
  const shown = names.slice(0, limit);
  const rest = names.length - shown.length;
  if (!shown.length) return "";
  if (rest > 0) return `${shown.join(", ")} and ${rest} more`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
};
